import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, leadingIcon, trailingIcon, ...props }, ref) => {
    if (!leadingIcon && !trailingIcon) {
      return (
        <input
          ref={ref}
          className={cn(
            "h-8 w-full rounded-[var(--radius-md)] border border-border-default bg-surface px-3 text-sm text-fg",
            "placeholder:text-fg-subtle",
            "transition-[border-color,box-shadow] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
            "hover:border-border-strong",
            "disabled:cursor-not-allowed disabled:bg-muted disabled:text-fg-disabled",
            className,
          )}
          {...props}
        />
      );
    }
    return (
      <div
        className={cn(
          "group relative flex h-8 w-full items-center rounded-[var(--radius-md)] border border-border-default bg-surface",
          "transition-[border-color,box-shadow] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
          "hover:border-border-strong",
          "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-border-focus",
          className,
        )}
      >
        {leadingIcon && (
          <span className="pl-2.5 pr-1 text-fg-subtle [&_svg]:size-4">
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle",
            leadingIcon ? "pl-0" : "pl-3",
            trailingIcon ? "pr-0" : "pr-3",
            "h-full",
          )}
          {...props}
        />
        {trailingIcon && (
          <span className="pr-2.5 pl-1 text-fg-subtle [&_svg]:size-4">
            {trailingIcon}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
